
import React, { useState, useEffect, useMemo } from 'react';
import { Box, Button, Table, TableBody, TableCell, TableHead, TableRow, TablePagination, TableSortLabel } from '@mui/material';
import ASTITitle from '../../labels/ASTITitle';
import ASTIText from '../../labels/ASTIText';

function compareValues(a, b) {
  const aNull = a === null || a === undefined || a === '';
  const bNull = b === null || b === undefined || b === '';

  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;

  const aNum = Number(a);
  const bNum = Number(b);
  const bothNumeric = Number.isFinite(aNum) && Number.isFinite(bNum) && String(a).trim() !== '' && String(b).trim() !== '';

  if (bothNumeric) {
    if (aNum < bNum) return -1;
    if (aNum > bNum) return 1;
    return 0;
  }

  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

export default function ABNPage() {
  const [status, setStatus] = useState('');
  const [mainRegister, setMainRegister] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState('date_added');
  const [sortOrder, setSortOrder] = useState('desc');

  const loadABNRegister = async () => {
    setLoading(true);
    setStatus('Loading ABN register from local database...');

    try {
      const response = await fetch('/api/abn');
      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}`);
      }
      const data = await response.json();
      setMainRegister(data || []);
      setStatus(`Loaded ${Array.isArray(data) ? data.length : 0} records from local ABN database.`);
    } catch (error) {
      console.error('Error loading ABN register:', error);
      setMainRegister([]);
      setStatus('Failed to load ABN register from local database.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadABNRegister();
  }, []);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ];
    const ext = file.name.split('.').pop().toLowerCase();
    if (!allowedTypes.includes(file.type) && !['xls', 'xlsx'].includes(ext)) {
      setStatus('Please upload a valid .xls or .xlsx file');
      return;
    }

    setLoading(true);
    setStatus('Uploading and importing…');

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch('/api/abn/import', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Server error during import');
      }

      const result = await response.json();
      setStatus(
        `Imported ${result.addedCount} new records, updated ${result.updatedCount}, ${result.duplicateCount} duplicates skipped.`
      );
      setMainRegister(result.mainRegister || []);
    } catch (err) {
      console.error('Error during import:', err);
      setStatus(`Error during import: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleReload = async () => {
    await loadABNRegister();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortBy(column);
    setSortOrder('asc');
    setPage(0);
  };

  const sortedRows = useMemo(() => {
    const rows = [...mainRegister];

    rows.sort((rowA, rowB) => {
      const result = compareValues(rowA?.[sortBy], rowB?.[sortBy]);
      return sortOrder === 'asc' ? result : -result;
    });

    return rows;
  }, [mainRegister, sortBy, sortOrder]);

  return (
    <Box
      width="100vw"
      minHeight="100vh"
      padding="20px"
      boxSizing="border-box"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="flex-start"
    >
      <ASTITitle text="ABN Import" />
      <ASTIText text="Upload an ABN file and view the ABN register in one place." />

      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2} mt={2}>
        <Button variant="contained" component="label" disabled={loading}>
          Upload Excel
          <input type="file" accept=".xls,.xlsx" hidden onChange={handleFileUpload} />
        </Button>
        <Button variant="outlined" onClick={handleReload} disabled={loading}>
          {loading ? 'Loading...' : 'Reload ABN Register'}
        </Button>
      </Box>

      {status && (
        <ASTIText text={status} />
      )}

      {mainRegister.length > 0 ? (
        <Box sx={{ width: '95%', marginTop: '20px', overflowX: 'auto', border: '1px solid #5C3520', borderRadius: '4px' }}>
          <Table sx={{ backgroundColor: '#FFFFFF', minWidth: '100%' }} size="small">
            <TableHead>
              <TableRow>
                {Object.keys(mainRegister[0]).map((key) => (
                  <TableCell
                    key={key}
                    sx={{
                      fontWeight: 'bold',
                      color: '#2B1810',
                      backgroundColor: '#F0E3D0',
                      padding: '8px',
                      fontSize: '12px',
                      whiteSpace: 'nowrap',
                      maxWidth: '150px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                    title={key}
                  >
                    <TableSortLabel
                      active={sortBy === key}
                      direction={sortBy === key ? sortOrder : 'asc'}
                      onClick={() => handleSort(key)}
                    >
                      {key}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => (
                <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#F0E3D0' } }}>
                  {Object.values(row).map((value, i) => (
                    <TableCell
                      key={i}
                      sx={{
                        color: '#2B1810',
                        padding: '8px',
                        fontSize: '12px',
                        maxWidth: '150px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={String(value)}
                    >
                      {String(value)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={sortedRows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ backgroundColor: '#F0E3D0' }}
          />
        </Box>
      ) : (
        !loading && <ASTIText text="No records found in ABN register." />
      )}
    </Box>
  );
}
